// 選択可能な「指標」の定義。地図の色分け・一覧のソートキーはすべてここから選ぶ。
// フィールドの取り方・表示フォーマット・色スケールの種類を 1 箇所にまとめ、
// filters / main / ssr / colorScale が同じ定義を参照する。
// 文言と数値フォーマットは言語で変わるので、getMetrics(lang) が解決済みの表を返す。
// 値の取り方（get）だけは言語に依らないため METRIC_GET として別に出し、
// 色 (colorScale) と順位 (rank) は文言を経由せずにそこだけを見る。
import type { Muni } from "../../schema/muni";
import { formatNumber, type Lang } from "./i18n";

export type MetricKey =
  | "flood"
  | "tsunami"
  | "sediment"
  | "quake"
  | "landPrice"
  | "distTokyo"
  | "distOsaka"
  | "distNagoya"
  | "tempJan"
  | "tempAug"
  | "population"
  | "foodStores";

// risk は「低い＝望ましい」の一方向、thermal は「低い＝寒い / 高い＝暑い」で両端が意味を持つ。
// 色の意味づけが違うので、どちらのスケールを使うかを指標の定義側に持たせる。
// amenity は risk の逆向きで「高い＝望ましい」（人口・店舗数）。
export type ScaleKind = "risk" | "thermal" | "amenity";

/** 望ましい向き。並び順（filters）と順位の言い回し（rank）がこれを見る。 */
export type Better = "low" | "high";

export interface MetricDef {
  key: MetricKey;
  label: string;
  short: string; // pill / チップ用の短縮ラベル
  unit: string;
  scale: ScaleKind;
  better: Better; // "high" の指標は一覧を降順に並べ、順位も「高い方から」で数える
  betterWord: string; // 望ましい端を指す形容詞。並び順の説明「◯◯順」に使う
  worseWord: string; // 望ましくない端を指す形容詞。順位ラベルと位置バーの両端表示に使う
  rankName: string; // ランキング・検索用の自然な表現（例: "水害・洪水リスクが低い"）
  hint: string; // 凡例に 1 行で出す「どちらが良いか」の説明
  get(muni: Muni): number | null;
  format(value: number): string;
}

interface MetricText {
  label: string;
  short: string;
  hint: string;
  betterWord: string;
  worseWord: string;
  rankName: string;
}

interface MetricSpec {
  key: MetricKey;
  unit: string;
  scale: ScaleKind;
  better: Better;
  get(muni: Muni): number | null;
  format(value: number, lang: Lang): string;
  text: Record<Lang, MetricText>;
}

function pct(v: number): string {
  return `${(v * 100).toFixed(1)}%`;
}

export function yen(v: number, lang: Lang): string {
  return lang === "ja"
    ? `${formatNumber(Math.round(v), lang)} 円/m2`
    : `${formatNumber(Math.round(v), lang)} JPY/m²`;
}

function km(v: number): string {
  return `${v.toFixed(1)} km`;
}

function celsius(v: number): string {
  return `${v.toFixed(1)} ℃`;
}

function people(v: number, lang: Lang): string {
  return lang === "ja" ? `${formatNumber(Math.round(v), lang)} 人` : `${formatNumber(Math.round(v), lang)} people`;
}

function shops(v: number, lang: Lang): string {
  return lang === "ja" ? `${formatNumber(Math.round(v), lang)} 店` : `${formatNumber(Math.round(v), lang)} stores`;
}

const SPECS: MetricSpec[] = [
  {
    key: "flood",
    unit: "%",
    scale: "risk",
    better: "low",
    get: (m) => m.hazard.floodL2Ratio,
    format: pct,
    text: {
      ja: { label: "洪水浸水想定区域 面積比", short: "洪水", hint: "低いほど浸水想定区域が小さい", betterWord: "低い", worseWord: "高い", rankName: "水害・洪水リスクが低い" },
      en: {
        label: "Flood inundation area ratio",
        short: "Flood",
        hint: "Lower means a smaller expected inundation area", betterWord: "lowest", worseWord: "highest", rankName: "lowest flood risk" },
    },
  },
  {
    key: "tsunami",
    unit: "%",
    scale: "risk",
    better: "low",
    get: (m) => m.hazard.tsunamiRatio,
    format: pct,
    text: {
      ja: { label: "津波浸水想定 面積比", short: "津波", hint: "低いほど浸水想定区域が小さい", betterWord: "低い", worseWord: "高い", rankName: "津波リスクが低い" },
      en: {
        label: "Tsunami inundation area ratio",
        short: "Tsunami",
        hint: "Lower means a smaller expected inundation area", betterWord: "lowest", worseWord: "highest", rankName: "lowest tsunami risk" },
    },
  },
  {
    key: "sediment",
    unit: "%",
    scale: "risk",
    better: "low",
    get: (m) => m.hazard.sedimentRatio,
    format: pct,
    text: {
      ja: { label: "土砂災害警戒区域 面積比", short: "土砂", hint: "低いほど警戒区域が小さい", betterWord: "低い", worseWord: "高い", rankName: "土砂災害リスクが低い" },
      en: {
        label: "Landslide alert area ratio",
        short: "Landslide",
        hint: "Lower means a smaller alert area", betterWord: "lowest", worseWord: "highest", rankName: "lowest landslide risk" },
    },
  },
  {
    key: "quake",
    unit: "%",
    scale: "risk",
    better: "low",
    get: (m) => m.hazard.quakeProb30y,
    format: pct,
    text: {
      ja: { label: "30年以内 震度6弱以上確率", short: "地震", hint: "低いほど強い揺れに見舞われにくい", betterWord: "低い", worseWord: "高い", rankName: "地震リスクが低い" },
      en: {
        label: "Shindo 6-lower or above in 30 years",
        short: "Quake",
        hint: "Lower means strong shaking is less likely", betterWord: "lowest", worseWord: "highest", rankName: "lowest earthquake risk" },
    },
  },
  {
    key: "landPrice",
    unit: "円/m2",
    scale: "risk",
    better: "low",
    get: (m) => m.landPriceMedianYenM2,
    format: yen,
    text: {
      ja: { label: "住宅地 地価中央値", short: "地価", hint: "低いほど安い", betterWord: "低い", worseWord: "高い", rankName: "住宅地地価が安い" },
      en: { label: "Median residential land price", short: "Land price", hint: "Lower means cheaper", betterWord: "lowest", worseWord: "highest", rankName: "cheapest residential land" },
    },
  },
  {
    key: "distTokyo",
    unit: "km",
    scale: "risk",
    better: "low",
    get: (m) => m.distKm.tokyo,
    format: km,
    text: {
      ja: { label: "東京駅からの距離", short: "東京から近い", hint: "低いほど東京駅に近い", betterWord: "低い", worseWord: "高い", rankName: "東京駅から近い" },
      en: { label: "Distance from Tokyo Station", short: "Near Tokyo", hint: "Lower means closer to Tokyo Station", betterWord: "lowest", worseWord: "highest", rankName: "closest to Tokyo Station" },
    },
  },
  {
    key: "distOsaka",
    unit: "km",
    scale: "risk",
    better: "low",
    get: (m) => m.distKm.osaka,
    format: km,
    text: {
      ja: { label: "大阪駅からの距離", short: "大阪から近い", hint: "低いほど大阪駅に近い", betterWord: "低い", worseWord: "高い", rankName: "大阪駅から近い" },
      en: { label: "Distance from Osaka Station", short: "Near Osaka", hint: "Lower means closer to Osaka Station", betterWord: "lowest", worseWord: "highest", rankName: "closest to Osaka Station" },
    },
  },
  {
    key: "distNagoya",
    unit: "km",
    scale: "risk",
    better: "low",
    get: (m) => m.distKm.nagoya,
    format: km,
    text: {
      ja: { label: "名古屋駅からの距離", short: "名古屋から近い", hint: "低いほど名古屋駅に近い", betterWord: "低い", worseWord: "高い", rankName: "名古屋駅から近い" },
      en: {
        label: "Distance from Nagoya Station",
        short: "Near Nagoya",
        hint: "Lower means closer to Nagoya Station", betterWord: "lowest", worseWord: "highest", rankName: "closest to Nagoya Station" },
    },
  },
  {
    key: "tempJan",
    unit: "℃",
    scale: "thermal",
    better: "high",
    get: (m) => m.tempMonthlyC[0] ?? null,
    format: celsius,
    text: {
      ja: { label: "1月 平均気温", short: "1月気温", hint: "高いほど冬があたたかい", betterWord: "高い", worseWord: "低い", rankName: "冬（1月）が暖かい" },
      en: { label: "Average temperature in January", short: "Jan temp", hint: "Higher means a milder winter", betterWord: "warmest", worseWord: "coldest", rankName: "warmest in winter (January)" },
    },
  },
  {
    key: "tempAug",
    unit: "℃",
    scale: "thermal",
    better: "low",
    get: (m) => m.tempMonthlyC[7] ?? null,
    format: celsius,
    text: {
      ja: { label: "8月 平均気温", short: "8月気温", hint: "低いほど夏がすずしい", betterWord: "低い", worseWord: "高い", rankName: "夏（8月）が涼しい" },
      en: { label: "Average temperature in August", short: "Aug temp", hint: "Lower means a cooler summer", betterWord: "lowest", worseWord: "highest", rankName: "coolest in summer (August)" },
    },
  },
  {
    key: "population",
    unit: "人",
    scale: "amenity",
    better: "high",
    // data/munis.json が旧世代（人口フィールドを持たない）でも undefined を null に寄せて壊れないようにする
    get: (m) => m.populationTotal ?? null,
    format: people,
    text: {
      ja: { label: "総人口", short: "人口", hint: "多いほど生活の場が集まっている", betterWord: "多い", worseWord: "少ない", rankName: "総人口が多い" },
      en: { label: "Population", short: "Population", hint: "Higher means more people and services nearby", betterWord: "highest", worseWord: "lowest", rankName: "largest population" },
    },
  },
  {
    key: "foodStores",
    unit: "店",
    scale: "amenity",
    better: "high",
    get: (m) => m.foodStoreCount ?? null,
    format: shops,
    text: {
      ja: {
        label: "食料品店数",
        short: "食料品店",
        hint: "多いほど買い物先が近くにある（スーパー・コンビニなど飲食料品を売る店の数）",
        betterWord: "多い",
        worseWord: "少ない",
        rankName: "食料品店が多い",
      },
      en: {
        label: "Food stores",
        short: "Food stores",
        hint: "Higher means more grocery and convenience stores within reach",
        betterWord: "highest",
        worseWord: "lowest",
        rankName: "most food stores",
      },
    },
  },
];

export const METRIC_ORDER: MetricKey[] = SPECS.map((s) => s.key);

/** 値の取り方だけの表。色・順位・ソートのように文言が要らない箇所はこちらを使う。 */
export const METRIC_GET = Object.fromEntries(SPECS.map((s) => [s.key, s.get])) as Record<
  MetricKey,
  (muni: Muni) => number | null
>;

/** 望ましい向きだけの表。並び順と順位の数え方はこれを見る（文言は要らない）。 */
export const METRIC_BETTER = Object.fromEntries(SPECS.map((s) => [s.key, s.better])) as Record<MetricKey, Better>;

const RESOLVED = new Map<Lang, Record<MetricKey, MetricDef>>();

/** 言語ごとに解決済みの指標表。1 ページの中で言語は変わらないので、言語単位でメモ化する。 */
export function getMetrics(lang: Lang): Record<MetricKey, MetricDef> {
  const cached = RESOLVED.get(lang);
  if (cached) return cached;

  const resolved = Object.fromEntries(
    SPECS.map((s) => [
      s.key,
      {
        key: s.key,
        label: s.text[lang].label,
        short: s.text[lang].short,
        unit: s.unit,
        scale: s.scale,
        better: s.better,
        betterWord: s.text[lang].betterWord,
        worseWord: s.text[lang].worseWord,
        rankName: s.text[lang].rankName,
        hint: s.text[lang].hint,
        get: s.get,
        format: (value: number) => s.format(value, lang),
      } satisfies MetricDef,
    ]),
  ) as Record<MetricKey, MetricDef>;

  RESOLVED.set(lang, resolved);
  return resolved;
}

export const DEFAULT_METRIC: MetricKey = "flood";

/**
 * 地価を並べるときに要求する最小の標本数。標本が数件しかない市区町村の中央値は
 * 1 地点の当たり外れで大きく動くため、ランキングとガイド記事の表からは外す
 * （市区町村の詳細ページには今までどおり出す）。
 */
export const LAND_PRICE_MIN_SAMPLE = 4;

/**
 * 全国順位（buildRankIndex / rankRows / 詳細ページの順位行）に載せてよいか。
 * 地価だけは標本が少ない中央値を外し、それ以外の指標はいつでも載せてよい。
 */
export function isRankable(metric: MetricKey, muni: Muni): boolean {
  if (metric !== "landPrice") return true;
  return muni.landPriceSampleN >= LAND_PRICE_MIN_SAMPLE;
}

export function isMetricKey(v: string): v is MetricKey {
  return (METRIC_ORDER as string[]).includes(v);
}
